import React, { Component } from 'react';
import { API } from 'aws-amplify';
import { connect } from 'react-redux';
import moment from 'moment';
import htmlToText from 'html-to-text';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Form from 'components/form';
import Input from 'components/form/Input';
import Select from 'components/form/Select';
import DateInput from 'components/form/DateInput';
import Button from 'components/common/Button';
import TIMEZONES from 'constant/timezones';
import Helmet from 'react-helmet';
import { withSnackbar } from 'hooks/useSnack';
import { validatePhone } from 'utils';
import PRODUCTS from 'constant/product-types';
import PhoneInput from 'components/form/PhoneInput';
import { GET_TEMPLATES } from 'graphql/timeline-templates';
import { CREATE_STUDENT_MILESTONES } from 'graphql/admin/students';
import { CREATE_CONVO } from 'graphql/conversations';
import { ADD_GUARDIAN } from 'graphql/student/Profile';
import { GET_USERS, GET_USER_BY_EMAIL } from 'graphql/admin/users';
import { INSERT_GUARDIAN } from 'graphql/guardian'

import client from 'graphql/client';

const FormItem = Form.FormItem;

const ROLES = [
  { label: 'Student', value: 'student' },
  { label: 'College Coach', value: 'lead_coach' },
  { label: 'Head Counselor', value: 'head_coach' },
  { label: 'Financial Aid Counselor', value: 'fa_coach' },
];

const YEAR_OPTIONS = [
  2021,
  2022,
  2023,
  2024,
  2025,
  2026,
  2027,
  2028,
  2029,
  2030,
].map((i) => ({ label: i, value: i }));

const PATHS = {
  student: '/students',
  lead_coach: '/lcs',
  head_coach: '/hcs',
};

const normalizeEmail =  (v, prev) => {
  if (v === prev) {
    return v;
  }
  return v.toLowerCase().trim();
}

const validateEmail = async (rule, value, callback) => {
  if (value) {
    const user = await getUserByEmail(value);
    if (user) {
      callback(new Error('An user exists with the email.'));
    } else {
      callback();
    }
  } else {
    callback();
  }
}

const getUserByEmail = async (email) => {
  const { users } = await client.query({
    query: GET_USER_BY_EMAIL,
    variables: { email }
  }).then(r => r.data);
  return users[0];
}

const GuardianForm = ({
  index,
  getFieldDecorator,
  getFieldValue,
  required,
}) => {
  const email = getFieldValue(`guardian_${index}_email`);

  const validateParentEmail = async (rule, value, callback) => {
    if (value) {
      const user = await getUserByEmail(value);
      if (user) {
        if (user.user_type !== 'parent') {
          callback(new Error('An user exists with the email but not a parent account'));
        }
      } else {
        callback();
      }
    } else {
      callback();
    }
  };

  return (
    <>
      <Typography variant="subtitle1">Guardian {index}</Typography>
      <Grid item>
        <FormItem>
          {getFieldDecorator(
            `guardian_${index}_first_name`,
            {
              rules: [{ required: required || !!email, message: 'Enter first name' }]
            },
          )(<Input placeholder="First name" />)}
        </FormItem>
      </Grid>
      <Grid item>
        <FormItem>
          {getFieldDecorator(
            `guardian_${index}_last_name`,
            {
              rules: [{ required: required || !!email, message: 'Enter Last name' }]
            },
          )(<Input placeholder="Last name" />)}
        </FormItem>
      </Grid>
      <Grid item>
        <FormItem>
          {getFieldDecorator(`guardian_${index}_email`, {
            validateTrigger: 'onBlur',
            normalize: normalizeEmail,
            rules: [
              { required, message: 'Email id required' },
              {type: 'email'},
              {validator: validateParentEmail},
            ],
          })(<Input placeholder="Email" />)}
        </FormItem>
      </Grid>
      <Grid item>
        <FormItem>
          {getFieldDecorator(`guardian_${index}_phone`, {
            rules: [
              { required, message: 'Enter mobile number' },
              { validator: validatePhone },
            ],
          })(<PhoneInput placeholder="Phone number" />)}
        </FormItem>
      </Grid>
      <Grid item>
        <FormItem>
          {getFieldDecorator(
            `guardian_${index}_relationship`,
            {
              rules: [{ required: required || !!email, message: 'Enter relationship' }]
            },
          )(<Input placeholder="Relationship to student" />)}
        </FormItem>
      </Grid>
    </>
  );
};

class AddUser extends Component {
  handleSubmit = () => {
    const { snackbar, form, history } = this.props;
    snackbar.info('Creating user account');
    return form.validateFields().then(
      async ({
        admin,
        parent_welcome_email,
        student_welcome_email,
        template,
        ...values
      }) => {
        values.username = values.email;
        values.admin_id = admin?.user_id;
        values.timezone = values.timezone?.value;
        values.trial_end_date =
          values.trial_end_date &&
          moment(values.trial_end_date).format('YYYY-MM-DD');

        try {
          const studentUser = await API.post('api', '/admin/users', {
            body: values,
          }).catch(({ response }) => {
            throw new Error(response.data.message);
          });

          if (values.usertype === 'student') {
            snackbar.info('Setting up milestones');
            await this.createMilestones(studentUser, template);

            const parent1Payload = {
              username: values.guardian_1_email,
              email: values.guardian_1_email,
              phone: values.guardian_1_phone,
              first_name: values.guardian_1_first_name,
              last_name: values.guardian_1_last_name,
              relationship: values.guardian_1_relationship,
            };

            const parent2Payload = {
              username: values.guardian_2_email,
              email: values.guardian_2_email,
              phone: values.guardian_2_phone,
              first_name: values.guardian_2_first_name,
              last_name: values.guardian_2_last_name,
              relationship: values.guardian_2_relationship,
            };

            snackbar.info('Creating parent account');
            if (parent1Payload.email) {
              await this.createGuardian(
                studentUser.user_id,
                parent1Payload,
                parent_welcome_email,
                admin,
              );
            }

            if (parent2Payload.email) {
              await this.createGuardian(
                studentUser.user_id,
                parent2Payload,
                parent_welcome_email,
                admin,
              );
            }

            snackbar.success('Student successfully created');
            history.push(`/students/${studentUser.user_id}/profile/general`);
          } else {
            snackbar.success('User created');
            history.push(PATHS[values.usertype]);
          }
        } catch (e) {
          snackbar.error(e.message);
        }
      },
      () => snackbar.error('Please validate inputs'),
    );
  };

  createGuardian = async (studentUserId, user, emailMessage, admin) => {
    const parent = await getUserByEmail(user.email);
    if (parent) {
      this.props.snackbar.info('Parent user exist, skipping account setup...');
      await client.mutate({
        mutation: INSERT_GUARDIAN,
        variables: {
          studentId: studentUserId,
          guardianId: parent.user_id,
          relationship: user.relationship,
        }
      });
    } else {
      await client.mutate({
        mutation: ADD_GUARDIAN,
        variables: {
          firstName: user.first_name,
          lastName: user.last_name,
          email: user.email,
          phone: user.phone,
          studentId: studentUserId,
          relationship: user.relationship,
        },
      });
      // await API.post('api', '/admin/users', {
      //   body: {
      //     username: user.email,
      //     email: user.email,
      //     usertype: 'parent',
      //     firstName: user.first_name,
      //     lastName: user.last_name,
      //   },
      // });
    }
  };

  createMilestones = async (user, template) => {
    const data = template.data.map((i) => ({
      name: i.name,
      order: i.order,
      user_id: user.user_id,
      student_milestone_exercises: {
        data: i.exercises.map((e) => ({
          user_id: user.user_id,
          order: e.order,
          exercise_id: e.exercise,
        })),
      },
    }));

    await client.mutate({
      mutation: CREATE_STUDENT_MILESTONES,
      variables: {
        data,
      },
    });
  };

  sendEmails = async (student_id, user_id, subject, message, admin) => {
    const payload = [];
    const studentConvo = {
      type: 'email',
      created_by: admin.user_id,
      student_id,
      subject,
      messages: {
        data: [
          {
            created_by: admin.user_id,
            type: 'email',
            html: message,
            text: htmlToText.fromString(message, {
              singleNewLineParagraphs: true,
            }),
            subject,
          },
        ],
      },
      users: {
        data: [{ user_id }],
      },
    };
    payload.push(studentConvo);
    await client.mutate({
      mutation: CREATE_CONVO,
      variables: {
        data: payload,
      },
    });
  };

  loadTimeLine = async () => {
    const {
      data: { rows },
    } = await client.query({
      query: GET_TEMPLATES,
      fetchPolicy: 'cache-first',
    });
    return rows.map((i) => ({ label: i.name, value: i.id, ...i }));
  };

  loadAdminUsers = async () => {
    const {
      data: { users },
    } = await client.query({
      query: GET_USERS,
      variables: {
        user_type: 'admin',
        includeChildren: false,
      },
    });
    return users.map((u) => ({
      ...u,
      label: u.user_first_name + ' ' + u.user_last_name,
      value: u.user_id,
    }));
  };

  render() {
    const {
      form: { getFieldDecorator, getFieldValue, setFieldsValue },
    } = this.props;

    const type = getFieldValue('usertype');

    return (
      <>
        <Helmet title="Create user" />
        <Card style={{ overflow: 'visible' }}>
          <CardContent>
            <Form>
              <Grid container spacing={3} md={10}>
                <Grid item md={6}>
                  <FormItem>
                    {getFieldDecorator('firstName', {
                      rules: [{ required: true }],
                    })(<Input disableMargin label="First name" />)}
                  </FormItem>
                </Grid>
                <Grid item md={6}>
                  <FormItem>
                    {getFieldDecorator('lastName', {
                      rules: [{ required: true }],
                    })(
                      <Input
                        disableMargin
                        label="Last name"
                      />,
                    )}
                  </FormItem>
                </Grid>
                <Grid item md={6}>
                  <FormItem>
                    {getFieldDecorator('email', {
                      normalize: normalizeEmail,
                      rules: [{ required: true }, { type: 'email' }, {validator: validateEmail}],
                    })(<Input disableMargin label="Email" />)}
                  </FormItem>
                </Grid>
                <Grid item md={6}>
                  <FormItem>
                    {getFieldDecorator('timezone', {
                      rules: [{ required: false }],
                    })(
                      <Select
                        noMargin
                        isMulti={false}
                        label="User timezone"
                        options={TIMEZONES}
                      />,
                    )}
                  </FormItem>
                </Grid>
                <Grid item md={6}>
                  <FormItem>
                    {getFieldDecorator('usertype', {
                      rules: [{ required: true }],
                    })(
                      <Select
                        noMargin
                        transformValue
                        isMulti={false}
                        label="User type"
                        options={ROLES}
                      />,
                    )}
                  </FormItem>
                </Grid>
                {type === 'student' && (
                  <>
                    <Grid item md={6}>
                      <FormItem>
                        {getFieldDecorator('product_type', {
                          rules: [{ required: true }],
                        })(
                          <Select
                            noMargin
                            transformValue
                            isMulti={false}
                            label="Product"
                            options={PRODUCTS}
                          />,
                        )}
                      </FormItem>
                    </Grid>
                    <Grid item md={6}>
                      <FormItem>
                        {getFieldDecorator('graduation_year', {
                          rules: [{ required: true }],
                        })(
                          <Select
                            noMargin
                            transformValue
                            isMulti={false}
                            label="Graduation Year"
                            options={YEAR_OPTIONS}
                          />,
                        )}
                      </FormItem>
                    </Grid>
                    <Grid item md={6}>
                      <FormItem>
                        {getFieldDecorator('trial_end_date', {
                          rules: [{ required: false }],
                        })(
                          <DateInput
                            clearable
                            fullWidth
                            format="L"
                            minDate={new Date()}
                            label="Trial end date"
                            placeholder="Optional"
                            inputVariant="outlined"

                            helpText="Setting this will create a trial account."
                          />,
                        )}
                      </FormItem>
                    </Grid>
                    <>
                      <Grid item md={2}>
                        <FormItem>
                          {getFieldDecorator('ccCredit', {
                            initialValue: 30,
                            rules: [{ required: true }],
                          })(
                            <Input
                              disableMargin
                              type="number"
                              label="CC credits"
                            />,
                          )}
                        </FormItem>
                      </Grid>
                      <Grid item md={2}>
                        <FormItem>
                          {getFieldDecorator('hcCredit', {
                            initialValue: 12,
                            rules: [{ required: true }],
                          })(
                            <Input
                              disableMargin
                              type="number"
                              label="HC credits"
                            />,
                          )}
                        </FormItem>
                      </Grid>
                      <Grid item md={2}>
                        <FormItem>
                          {getFieldDecorator('faCredit', {
                            initialValue: 2,
                            rules: [{ required: true }],
                          })(
                            <Input
                              disableMargin
                              type="number"
                              label="FA credits"
                            />,
                          )}
                        </FormItem>
                      </Grid>
                      <Grid item md={3}>
                        <FormItem>
                          {getFieldDecorator('ccEssayCredit', {
                            initialValue: 16,
                            rules: [{ required: true }],
                          })(
                            <Input
                              disableMargin
                              type="number"
                              label="CC Essay credits"
                            />,
                          )}
                        </FormItem>
                      </Grid>
                      <Grid item md={3}>
                        <FormItem>
                          {getFieldDecorator('hcEssayCredit', {
                            initialValue: 8,
                            rules: [{ required: true }],
                          })(
                            <Input
                              disableMargin
                              type="number"
                              label="HC Essay credits"
                            />,
                          )}
                        </FormItem>
                      </Grid>
                    </>
                    <Grid item md={6}>
                      <FormItem>
                        {getFieldDecorator('template', {
                          rules: [{ required: true }],
                        })(
                          <Select
                            async
                            defaultOptions
                            isMulti={false}
                            label="Select timeline template"
                            loadOptions={this.loadTimeLine}
                          />,
                        )}
                      </FormItem>
                    </Grid>
                    <Grid item md={6}>
                      <FormItem>
                        {getFieldDecorator('admin', {
                          rules: [{ required: true }],
                        })(
                          <Select
                            async
                            defaultOptions
                            isMulti={false}
                            label="Select Admin handling this account"
                            loadOptions={this.loadAdminUsers}
                          />,
                        )}
                      </FormItem>
                    </Grid>
                    <Grid item md={12}>
                      <Typography gutterBottom variant="h6">
                        Guardians
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid item md={6}>
                          <GuardianForm
                            index={1}
                            getFieldDecorator={getFieldDecorator}
                            getFieldValue={getFieldValue}
                            setFieldsValue={setFieldsValue}
                          />
                        </Grid>
                        <Grid item md={6}>
                          <GuardianForm
                            index={2}
                            getFieldDecorator={getFieldDecorator}
                            getFieldValue={getFieldValue}
                            setFieldsValue={setFieldsValue}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </>
                )}
                <Grid item md={12}>
                  <Button
                    size="large"
                    variant="contained"
                    color="primary"
                    onClick={this.handleSubmit}
                  >
                    Create
                  </Button>
                </Grid>
              </Grid>
            </Form>
          </CardContent>
        </Card>
      </>
    );
  }
}

export default connect((s) => s.user)(Form.create()(withSnackbar(AddUser)));
